import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import { useAuthStore } from '../../stores/authStore';
import { useRoleStore } from '../../stores/roleStore';
import { useAthleteProgression } from '../../hooks/useProgression';
import AppHeader from '../../components/common/AppHeader';
import { beltColors } from '../../theme';
import { formatDate } from '../../utils/formatters';
import SetStartingRankCard from '../../components/training/SetStartingRankCard';
import { useTranslation } from 'react-i18next';

export default function TrainingScreen() {
  const { t, i18n } = useTranslation();
  const colors = useAppColors();
  const user = useAuthStore((s) => s.user);
  const activeProfile = useRoleStore((s) => s.activeProfile);
  const userId = user?.id || '';
  const athleteId = activeProfile?.id || userId;

  const { data: progression } = useAthleteProgression(athleteId);

  const beltName = progression?.current_belt?.name || 'belts.white';
  // Belt name is an i18n key (e.g. 'belts.white') — extract the color key for lookups
  const beltColorKey = beltName.includes('.') ? beltName.split('.').pop()! : beltName;
  const stripes = progression?.current_stripes ?? 0;
  const daysInBelt = progression?.days_in_current_belt ?? 0;
  const monthsInBelt = Math.floor(daysInBelt / 30);
  const lastPromoDate = progression?.history?.[0]?.promotion_date;
  const lastPromoFormatted = lastPromoDate
    ? formatDate(lastPromoDate, i18n.language)
    : '--';
  const beltColor = beltColors[beltColorKey] || beltColors.white;
  const isWhiteBelt = beltColorKey === 'white';
  const isBlackBelt = beltColorKey === 'black';
  const rankBarColor = isBlackBelt ? '#c41e1e' : '#1a1a1a';

  return (
    <View style={[styles.screen, { backgroundColor: colors.bg }]}>
      <AppHeader />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, responsiveContentStyle]}
        showsVerticalScrollIndicator={false}
      >
        <Text style={[styles.title, { color: colors.text }]}>
          {t('training.title')}
        </Text>

        {/* One-time starting-rank declaration (no promotion history yet) */}
        {progression && (!progression.history || progression.history.length === 0) ? (
          <SetStartingRankCard key={athleteId} athleteId={athleteId} />
        ) : null}

        {/* Belt Status Card */}
        <View
          style={[
            styles.beltCard,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
        >
          <View style={styles.beltHeader}>
            <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
              {t('training.currentRank')}
            </Text>
            <View style={[styles.stripeBadge, { backgroundColor: colors.border }]}>
              <Text style={[styles.stripeBadgeText, { color: colors.text }]}>
                {t('training.stripe', { count: stripes })}
              </Text>
            </View>
          </View>

          {/* Belt Visual */}
          <View
            style={[
              styles.beltVisual,
              {
                backgroundColor: beltColor,
                borderWidth: isWhiteBelt ? 1 : 0,
                borderColor: colors.border,
              },
            ]}
          >
            <View style={[styles.beltRankBar, { backgroundColor: rankBarColor }]}>
              {Array.from({ length: stripes }).map((_, i) => (
                <View key={i} style={styles.beltStripe} />
              ))}
            </View>
          </View>

          {/* Belt Stats */}
          <View style={styles.beltStats}>
            <View style={styles.beltStatItem}>
              <Text style={[styles.statLabel, { color: colors.textTertiary }]}>
                {t('training.timeInBelt')}
              </Text>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {monthsInBelt > 0 ? t('training.months', { count: monthsInBelt }) : t('training.days', { count: daysInBelt })}
              </Text>
            </View>
            <View style={styles.beltStatItem}>
              <Text style={[styles.statLabel, { color: colors.textTertiary }]}>
                {t('training.lastPromo')}
              </Text>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {lastPromoFormatted}
              </Text>
            </View>
          </View>
        </View>

      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  scroll: { flex: 1 },
  content: { padding: 24, paddingBottom: 120, gap: 24 },

  title: {
    fontSize: 24,
    fontWeight: '700',
  },

  // Section label
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  section: {
    gap: 12,
  },

  // Belt Card
  beltCard: {
    borderRadius: 24,
    padding: 24,
    borderWidth: 1,
    gap: 16,
  },
  beltHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stripeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  stripeBadgeText: {
    fontSize: 12,
  },

  // Belt Visual
  beltVisual: {
    height: 48,
    borderRadius: 4,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  beltRankBar: {
    width: 80,
    height: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 5,
    paddingHorizontal: 8,
    marginLeft: 'auto',
    marginRight: 18,
  },
  beltStripe: {
    width: 5,
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 1,
  },

  // Belt stats
  beltStats: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },
  beltStatItem: {
    flex: 1,
    gap: 4,
  },
  statLabel: {
    fontSize: 11,
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: 18,
    fontWeight: '700',
  },

  // Notes Card
  notesCard: {
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    gap: 12,
  },
  quoteBlock: {
    borderLeftWidth: 2,
    paddingLeft: 12,
  },
  quoteText: {
    fontSize: 14,
    fontStyle: 'italic',
    lineHeight: 20,
  },
});
